/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file driverPushRouting.js
 * @description
 * Side-effecting half of the Driver push handling (the pure half is
 * {@link module:helpers/driverPushPayload}):
 *
 *  - {@link openDriverTarget} — one-tap routing of a resolved `Driver*` target.
 *    Already in driver mode → navigate. In another mode but allowed → persist the
 *    tap (`savePendingOpenNotification`), switch mode via `applyHomeLanding`
 *    (full root remount) and let the driver home's `PushNotificationsBootstrap`
 *    replay it. Not allowed → a toast and nothing else (never a half-open screen).
 *  - {@link handleDriverForegroundPush} — a push that arrives while the driver is
 *    in the app: refresh the data caches, buzz, and hand it to the banner host.
 *
 * @module helpers/driverPushRouting
 */

import {Vibration} from 'react-native';
import Toast from 'react-native-toast-message';
import i18n from '../../i18n';
import {filterModesForAppVariant, isAppModeEnabled} from '../config/appVariant';
import {savePendingOpenNotification} from '../services/pushNotifications';
import {getAvailableAppModesForUser} from './managerRoles';
import {applyHomeLanding} from './homeLanding';
import {getSetting, getSettingJSON} from './persistentSettings';
import {getUserInfo} from './storage';
import {buildDriverPushPayload, isDriverPushData} from './driverPushPayload';
import {emitDriverPush, emitDriverRefresh} from './driverPushBus';

/**
 * @param {?{name?: string}} target Resolved navigation target.
 * @returns {boolean} True for targets inside the driver module.
 */
export const isDriverTarget = (target) => String(target?.name || '').startsWith('Driver');

/**
 * Whether the signed-in user may use the driver mode (role entitlement + build).
 * @returns {Promise<boolean>} True when the mode can be switched to.
 */
export const canUseDriverMode = async () => {
    try {
        const [user, place] = await Promise.all([getUserInfo(), getSettingJSON('restaurantPlace', null)]);
        const modes = filterModesForAppVariant(getAvailableAppModesForUser(user, place));

        return modes.includes('driver') && isAppModeEnabled('driver');
    } catch (_error) {
        return false;
    }
};

/**
 * Open a driver target with one tap, switching into driver mode when needed.
 *
 * @param {{name: string, params?: Object}} target Resolved target (`Driver*`).
 * @param {{data?: Object, notification?: Object}} message Original message, persisted for the post-switch replay.
 * @param {(name: string, params?: Object) => (boolean|void)} navigate Navigation callback.
 * @returns {Promise<'navigated'|'switching'|'denied'>} What happened.
 */
export const openDriverTarget = async (target, message, navigate) => {
    const mode = await getSetting('userAppMode');

    if (mode === 'driver') {
        emitDriverRefresh();
        navigate(target.name, target.params || {});

        return 'navigated';
    }

    if (!(await canUseDriverMode())) {
        Toast.show({
            type: 'info',
            text1: i18n.t('driver_push_mode_denied', {defaultValue: 'Ehhez a Sofőr / Futár módhoz nincs jogosultságod.'}),
        });

        return 'denied';
    }

    try {
        await savePendingOpenNotification(message);
        Toast.show({type: 'info', text1: i18n.t('driver_push_switching', {defaultValue: 'Átváltás a Sofőr módra…'})});
        await applyHomeLanding({mode: 'driver'});
    } catch (_error) {
        return 'denied';
    }

    return 'switching';
};

/**
 * Foreground arrival of a driver push. Returns true when it was shown in-app
 * (screen banner or the global banner host), false when the caller should fall
 * back to the normal local notification (not in driver mode / not a driver push).
 *
 * @param {{data?: Object, notification?: Object}} message Normalized FCM message.
 * @returns {boolean} True when handled in-app.
 */
export const handleDriverForegroundPush = (message) => {
    if (!isDriverPushData(message?.data)) {
        return false;
    }
    const consumed = emitDriverPush(buildDriverPushPayload(message));

    if (consumed) {
        emitDriverRefresh();
        try {
            Vibration.vibrate([0, 250, 120, 250]);
        } catch (_error) {
            // Haptics are a nicety, never a failure.
        }
    }

    return consumed;
};
